import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { FirebaseError } from 'firebase/app';
import type { User } from 'firebase/auth';
import { auth } from '@/firebase';
import logoutProxy from '@/serverProxy/logout';
import router from '@/router';
import i18n from '@/i18n';
import HttpRedirectError from '@/utils/HttpRedirectError';
import ResponseLostError from '@/utils/ResponseLostError';
import { setErrorDetails } from '@/views/Errors/errorDetails';
import { stripStatusPrefix } from '@/utils/errorMessages';

const host =
  import.meta.env.MODE === 'development'
    ? `http://${window.location.hostname}:8081`
    : '';

const axiosInstance = axios.create({
  baseURL: `${host}/api/v2`,
});

declare module 'axios' {
  interface AxiosRequestConfig {
    // Background requests (the admin badge poll) set this so a 401, 403 or
    // 404 rejects with HttpRedirectError without sending the visitor to the
    // error page.
    skipErrorPage?: boolean;
  }
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  authRetried?: boolean;
}

// One forced refresh at a time: when several requests fail with 401 at once,
// they all wait on the same refresh instead of each asking for a new token.
let refreshInFlight: Promise<string> | null = null;

const forceTokenRefresh = (user: User): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = user.getIdToken(true).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Sign out a session that can no longer authenticate, so the visitor sees
// the logged-out site and can log in again. The auth listener in
// authSession.ts clears the store when the sign-out lands.
const endSession = async () => {
  try {
    await logoutProxy.logout();
  } catch {
    // Signing out of an already broken session can fail; the 401 page
    // still follows.
  }
};

// A token refresh failure that ends the session, for whoever is debugging
// in the browser console.
const logSessionEnd = (err: unknown) => {
  const { code, message } = (err ?? {}) as FirebaseError;
  // eslint-disable-next-line no-console
  console.error('Token refresh failed; signing out:', code, message);
};

// Send the visitor to an error page. The details live in memory (see
// views/Errors/errorDetails.ts), never in the URL or history state.
const showErrorPage = (
  name: string,
  message: string | undefined,
  params?: { pathMatch: string[] }
) => {
  setErrorDetails(name, message);
  router.replace({ name, ...(params ? { params } : {}) });
};

const SESSION_EXPIRED = 'Your session has expired. Please log in again.';
const NETWORK_ERROR = 'Network error. Please check your connection.';
const TOO_LARGE_ERROR =
  'The request is too large: photos can be at most 25 MB and the text import 25 MB.';
const GATEWAY_ERROR =
  'The server did not answer; the request may or may not have completed.';

// Rate limits on the token service: wait a second and try the refresh once
// more before giving up on this request.
const RATE_LIMIT_CODES = ['auth/quota-exceeded', 'auth/too-many-requests'];
const RATE_LIMIT_RETRY_MS = 1000;

// Token refresh failures that say nothing about the session itself. They
// keep the user signed in; the request fails and can be tried again.
const TRANSIENT_AUTH_CODES = [
  ...RATE_LIMIT_CODES,
  'auth/network-request-failed',
];

const isTransientAuthError = (err: unknown) =>
  TRANSIENT_AUTH_CODES.includes((err as FirebaseError)?.code);

const refreshWithBackoff = async (user: User): Promise<string> => {
  try {
    return await forceTokenRefresh(user);
  } catch (err) {
    if (!RATE_LIMIT_CODES.includes((err as FirebaseError)?.code)) {
      throw err;
    }
    await new Promise(resolve => {
      setTimeout(resolve, RATE_LIMIT_RETRY_MS);
    });
    return forceTokenRefresh(user);
  }
};

axiosInstance.interceptors.response.use(
  res => res,
  async (error: AxiosError<{ message?: string; code?: string }> | Error) => {
    // Errors thrown by the request interceptor below arrive here unchanged.
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }
    const config = error.config as RetryableRequestConfig | undefined;
    const status = error.response?.status;
    const message = error.response?.data?.message || undefined;
    const unknownUser = error.response?.data?.code === 'unknown-user';
    let signedOut = false;

    // The backend answers 401 for an expired or revoked token. Force a fresh
    // token and retry once; only a request that carried a token is worth
    // retrying. A token for a uid with no user row (code "unknown-user")
    // cannot be fixed by a refresh, and the refresh would refetch the user
    // through onIdTokenChanged and loop.
    if (status === 401 && config && config.headers.has('Authorization')) {
      // The session already ended (another request signed out, or the
      // visitor logged out during a refresh). Whoever ended it already chose
      // the page to show, so reject quietly.
      const sessionGone = () =>
        Promise.reject(new HttpRedirectError(401, SESSION_EXPIRED));
      if (!auth.currentUser) {
        return sessionGone();
      }
      if (!config.authRetried && !unknownUser) {
        config.authRetried = true;
        let refreshed = false;
        try {
          await refreshWithBackoff(auth.currentUser);
          refreshed = true;
        } catch (err) {
          if (isTransientAuthError(err)) {
            return Promise.reject(new Error(NETWORK_ERROR));
          }
          // Otherwise fall through to ending the session below. Console only:
          // the backend may be what is failing.
          logSessionEnd(err);
        }
        if (refreshed) {
          return auth.currentUser ? axiosInstance(config) : sessionGone();
        }
      }
      // A fresh token did not help, or the refresh failed for good. Sign out
      // so the visitor is not stuck on the 401 page with a session that
      // cannot log in or out.
      await endSession();
      signedOut = true;
    }

    // The error page title already names the status, so drop the backend's
    // "401 Error: " prefix from the details it shows. A signed-out session
    // reads the same as the request-interceptor path, except "Unknown user",
    // which names the fix (an account with no user row).
    let pageMessage = message ? stripStatusPrefix(message) : undefined;
    if (signedOut && !unknownUser) {
      pageMessage = SESSION_EXPIRED;
    }

    if (status === 401 || status === 403) {
      // skipErrorPage keeps background requests quiet, but a session that
      // just ended is not a background condition: show it.
      if (!config?.skipErrorPage || signedOut) {
        showErrorPage(String(status), pageMessage);
      }
      return Promise.reject(new HttpRedirectError(status, pageMessage));
    }

    if (status === 404) {
      // Keep the visited path in the URL; the 404 route is the catch-all.
      // route.path is percent-encoded and the router encodes params again,
      // so decode it first.
      const { path } = router.currentRoute.value;
      let decodedPath = path;
      try {
        decodedPath = decodeURI(path);
      } catch {
        // Malformed encoding: keep the raw path.
      }
      const pathMatch = decodedPath.substring(1).split('/');
      if (!config?.skipErrorPage) {
        showErrorPage('404', pageMessage, { pathMatch });
      }
      return Promise.reject(new HttpRedirectError(404, pageMessage));
    }

    // No response at all (offline, CORS, timeout): say so instead of
    // rejecting with an empty message. That and a gateway error leave the
    // request's outcome unknown, which ResponseLostError tells callers.
    if (!error.response) {
      return Promise.reject(new ResponseLostError(NETWORK_ERROR));
    }
    // A gateway error from nginx or the load balancer (an HTML page, no
    // backend JSON message) leaves the outcome unknown. The backend's own
    // 503 (JSON with a message, e.g. when it cannot verify tokens) means the
    // request was refused, so it stays a normal failure.
    if (
      (status === 502 || status === 503 || status === 504) &&
      typeof error.response.data?.message !== 'string'
    ) {
      return Promise.reject(new ResponseLostError(GATEWAY_ERROR));
    }
    // nginx (HTML) and express-fileupload (plain text) answer 413 without
    // the backend's JSON message; so can any other proxy error.
    if (status === 413) {
      return Promise.reject(new Error(message || TOO_LARGE_ERROR));
    }
    // The backend's machine-readable reason, when it sends one.
    return Promise.reject(
      Object.assign(
        new Error(message || `The request failed (status ${status}).`),
        error.response?.data?.code ? { code: error.response.data.code } : {}
      )
    );
  }
);

axiosInstance.interceptors.request.use(async config => {
  const { currentUser } = auth;
  let token: string | undefined;
  if (currentUser) {
    try {
      // The SDK returns its cached token and refreshes it once it expires, so
      // every request carries a valid token without a page reload.
      token = await currentUser.getIdToken();
    } catch (err) {
      if (isTransientAuthError(err)) {
        throw new Error(NETWORK_ERROR);
      }
      logSessionEnd(err);
      // The session cannot mint a token (revoked, disabled, deleted). Do not
      // send the request anonymously; end the session instead.
      // Shown even for skipErrorPage requests: the session has ended.
      await endSession();
      showErrorPage('401', SESSION_EXPIRED);
      throw new HttpRedirectError(401, SESSION_EXPIRED);
    }
  }

  if (token) {
    config.headers.set('Authorization', token);
    config.headers.set('Locale', i18n.global.locale.value);
  } else {
    // A retried config still holds the previous attempt's headers.
    config.headers.delete('Authorization');
  }
  return config;
});

export default axiosInstance;
